import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { publicApi } from '../utils/publicApi';

const PARTNER_TYPE_ORDER = ['Strategic Partner', 'Official Partner', 'Supporting Partner', 'Media Partner'];

const sortPartnerTypeKey = (a, b) => {
  const ia = PARTNER_TYPE_ORDER.indexOf(a);
  const ib = PARTNER_TYPE_ORDER.indexOf(b);
  const sa = ia === -1 ? 999 : ia;
  const sb = ib === -1 ? 999 : ib;
  if (sa !== sb) return sa - sb;
  return String(a).localeCompare(String(b));
};

const sponsorImageSrc = (sponsor) => {
  if (sponsor.img_sponsor && String(sponsor.img_sponsor).trim() !== '') {
    let imageSource = sponsor.img_sponsor.startsWith('@')
      ? sponsor.img_sponsor.substring(1).trim()
      : sponsor.img_sponsor.trim();
    return publicApi.toAbsoluteUrl(imageSource);
  }
  return `https://placehold.co/400x200/f8fafc/64748b?text=${encodeURIComponent(sponsor.name || '')}`;
};

const normalizeHref = (link) => {
  const s = String(link || '').trim();
  if (!s) return null;
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  if (s.startsWith('//')) return `https:${s}`;
  if (s.startsWith('/')) return s;
  return `https://${s}`;
};

const PartnerSlot = ({ sponsor, imageSource, visitLabel }) => {
  const href = normalizeHref(sponsor.link);
  const label = `${sponsor.name}${href ? ` — ${visitLabel}` : ''}`;

  const inner = (
    <div className="flex w-[204px] max-w-full flex-col items-center gap-4">
      <div
        className="relative w-full max-w-[192px] aspect-[192/184] overflow-hidden rounded-2xl border border-[#E8E8EF] bg-white p-4 shrink-0"
        style={{ boxShadow: '2px 4px 9.8px rgba(144, 144, 144, 0.25)' }}
      >
        <img
          src={imageSource}
          alt=""
          className="h-full w-full object-contain"
          loading="lazy"
          decoding="async"
        />
      </div>
      {sponsor.name ? (
        <span
          className="line-clamp-2 w-full text-center text-[14px] font-semibold leading-5 text-[#51516B]"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          {sponsor.name}
        </span>
      ) : null}
    </div>
  );

  if (href) {
    const external = href.startsWith('http');
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className="group block rounded-xl outline-none transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#492E8B] focus-visible:ring-offset-2"
        aria-label={label}
      >
        {inner}
      </a>
    );
  }

  return (
    <div className="rounded-xl" aria-label={sponsor.name || undefined}>
      {inner}
    </div>
  );
};

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const { t, isRTL } = useLanguage();

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        setLoading(true);
        const data = await publicApi.getSponsors();
        setError(null);
        setSponsors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching sponsors:', err);
        setError(err.message);
        setSponsors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of sponsors) {
      const key = (s.partner_type && String(s.partner_type).trim()) || t('website.sponsors.defaultGroup');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return Array.from(map.entries()).sort(([a], [b]) => sortPartnerTypeKey(a, b));
  }, [sponsors, t]);

  const partnerTypes = useMemo(() => grouped.map(([type]) => type), [grouped]);

  const filteredSponsors = useMemo(() => {
    if (typeFilter === 'all') return sponsors;
    return sponsors.filter((s) => {
      const key = (s.partner_type && String(s.partner_type).trim()) || t('website.sponsors.defaultGroup');
      return key === typeFilter;
    });
  }, [sponsors, t, typeFilter]);

  return (
    <div
      className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-28"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div className="relative z-10 mx-auto w-full max-w-[1280px] px-6 sm:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="mb-14 flex flex-col items-center gap-3 text-center lg:mb-20 lg:gap-4"
        >
          <h2 className="w-full text-[32px] font-bold leading-tight sm:text-[40px] lg:text-[48px]" style={{ color: '#51516B', lineHeight: 1.2 }}>
            {t('website.sponsors.title')}
          </h2>
        </motion.div>

        {partnerTypes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mb-10 flex flex-wrap items-center justify-center gap-2 sm:mb-12 sm:gap-3"
            style={{ gap: 'clamp(8px, 2vw, 49px)' }}
          >
            <button
              type="button"
              onClick={() => setTypeFilter('all')}
              className="min-w-[100px] px-4 py-[15px] rounded-[37px] flex items-center justify-center transition-colors duration-200"
              style={{
                background:
                  typeFilter === 'all'
                    ? 'linear-gradient(157deg, #0AB0CE 0%, #111034 100%)'
                    : 'transparent',
              }}
            >
              <span
                className="text-[15px] sm:text-[16px] font-semibold"
                style={{ color: typeFilter === 'all' ? '#EDEAF3' : '#51516B' }}
              >
                {t('website.sponsors.filterAll')}
              </span>
            </button>

            {partnerTypes.map((type) => {
              const active = typeFilter === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setTypeFilter(type)}
                  className="min-w-[100px] px-4 py-[15px] rounded-[37px] flex items-center justify-center transition-colors duration-200"
                  style={{
                    background: active
                      ? 'linear-gradient(157deg, #0AB0CE 0%, #111034 100%)'
                      : 'transparent',
                  }}
                >
                  <span
                    className="text-[15px] sm:text-[16px] font-semibold text-center"
                    style={{ color: active ? '#EDEAF3' : '#51516B' }}
                  >
                    {type}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}

        {loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-16 text-center">
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#54BBDE]/25">
              <svg className="h-8 w-8 animate-spin text-[#492E8B]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <p className="font-medium text-[#51516B]">{t('website.sponsors.loading')}</p>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-xl border border-red-100 bg-white py-16 text-center shadow-sm"
          >
            <p className="font-medium text-red-600">
              {t('website.sponsors.errorPrefix')} {error}
            </p>
          </motion.div>
        )}

        {!loading && !error && grouped.length > 0 && (
          <motion.div
            key={typeFilter}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="flex w-full flex-wrap justify-center gap-y-[22px] gap-x-[38px]"
          >
            {filteredSponsors.map((sponsor) => (
              <PartnerSlot
                key={sponsor.id}
                sponsor={sponsor}
                imageSource={sponsorImageSrc(sponsor)}
                visitLabel={t('website.sponsors.visit')}
              />
            ))}
          </motion.div>
        )}

        {!loading && !error && sponsors.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-xl border border-gray-100 bg-white py-16 text-center shadow-sm"
          >
            <p className="font-medium text-[#51516B]">{t('website.sponsors.empty')}</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Sponsors;
